import {
  AppointmentHistoryData,
  AppointmentReviewData,
  ICreateAppointmentArgs,
  LocationOpeningsData,
  IRescheduleAppointmentArgs,
  UserData,
  IServiceRequestArgs,
  ICreatePaymentMethodArgs,
  UserOpeningsData,
  LocationData,
  BlogData,
  BlogListData,
  UserExperienceResponse,
  Location,
  PhotosResponse,
  PagedResult,
  Service,
  ServiceOption,
} from "./types";
import {
  addOpeningsBackedAppointmentNote,
  createOpeningsBackedAppointment,
  createOpeningsBackedPaymentMethod,
  createOpeningsBackedServiceRequest,
  getOpeningsBackedAppointmentReview,
  getOpeningsBackedAppointmentHistory,
  getOpeningsBackedCustomerGate,
  getOpeningsBackedVerifiedAppointmentHistory,
  getOpeningsBackedServicePricing,
  getOpeningsBackedStylistExperience,
  getOpeningsBackedUserNextOpenings,
  getOpeningsBackedUserOpenings,
  getOpeningsBackedLocationOpenings,
  getOpeningsBackedLocation,
  getOpeningsBackedLocations,
  getOpeningsBackedPhotos,
  getOpeningsBackedPhotoCategories,
  getOpeningsBackedLikedPhotoIds,
  getOpeningsBackedServices,
  getOpeningsBackedServiceStylists,
  getOpeningsBackedServedCount,
  getOpeningsBackedTeam,
  getOpeningsBackedUser,
  rescheduleOpeningsBackedAppointment,
  sendOpeningsBackedVerificationCode,
  verifyOpeningsBackedVerificationCode,
} from "./openingsLocations";
import {
  getOpeningsBackedBlogArticle,
  getOpeningsBackedBlogArticles,
} from "./openingsBlog";
import type { EditionBusinessKey } from "./openingsBusiness";
import type { OpeningsCreateAppointmentResult } from "./openingsTypes";
import type { AppointmentForm } from "@/lib/hooks/useAppointment";

import { OpeningsJson } from "@/lib/api/openingsClient";

export class EditionJson {
  static recordPhotoViews = OpeningsJson.recordPhotoViews;

  static getLocation = async (pathId: string): Promise<LocationData> =>
    getOpeningsBackedLocation(pathId);

  static getLocations = async (): Promise<Location[]> =>
    getOpeningsBackedLocations();

  static getPhotos = async (params: {
    limit: number;
    cursor?: string;
    category: string;
    business?: EditionBusinessKey;
    deviceId?: string;
  }): Promise<PagedResult<PhotosResponse>> => getOpeningsBackedPhotos(params);

  static getPhotoCategories = async (
    business?: EditionBusinessKey,
  ): Promise<{ key: string; label: string }[]> =>
    getOpeningsBackedPhotoCategories(business);

  static getLikedPhotoIds = async (
    deviceId: string,
    business?: EditionBusinessKey,
  ): Promise<string[]> => getOpeningsBackedLikedPhotoIds(deviceId, business);

  static getTeam = async (business?: EditionBusinessKey) =>
    getOpeningsBackedTeam(business);

  static getServices = async (business?: EditionBusinessKey) =>
    getOpeningsBackedServices(business);

  static getServiceStylists = async (
    serviceTitle: string,
    business?: EditionBusinessKey,
  ) => getOpeningsBackedServiceStylists(serviceTitle, business);

  static getServedAppointmentCount = async (
    business?: EditionBusinessKey,
  ): Promise<number> => getOpeningsBackedServedCount(business);

  static getLocationOpeningsByServices = async (
    locationId: string,
    date: string,
    services: (Service & { option?: ServiceOption })[],
  ): Promise<LocationOpeningsData[]> =>
    getOpeningsBackedLocationOpenings(locationId, date, services);

  static getAppointment = async (
    appointmentId: string,
  ): Promise<AppointmentReviewData> =>
    getOpeningsBackedAppointmentReview(appointmentId);

  static getUser = async (pathId: string): Promise<UserData> =>
    getOpeningsBackedUser(pathId);

  static getOpeningsByDuration = async (
    pathId: string,
    date: string,
    services: (Service & { option?: ServiceOption })[],
  ): Promise<UserOpeningsData[]> =>
    getOpeningsBackedUserOpenings(pathId, date, services);

  static getUserNextOpeningsByDuration = async (
    pathId: string,
    date: string,
    services: (Service & { option?: ServiceOption })[],
  ): Promise<(UserOpeningsData & { date: string })[]> =>
    getOpeningsBackedUserNextOpenings(pathId, date, services);

  static createAppointment = async (
    args: ICreateAppointmentArgs,
  ): Promise<OpeningsCreateAppointmentResult> =>
    createOpeningsBackedAppointment(args);

  static rescheduleAppointment = async (
    args: IRescheduleAppointmentArgs,
  ): Promise<{ appointmentId: string }> =>
    rescheduleOpeningsBackedAppointment(args);

  static getAppointmentHistory = async (
    phoneNumber: string,
    userId: string,
    appointment?: Pick<
      AppointmentForm,
      "userId" | "locationId" | "locationName" | "scheduleId"
    >,
  ): Promise<AppointmentHistoryData> =>
    getOpeningsBackedAppointmentHistory(phoneNumber, userId, appointment);

  /**
   * Returns true when the customer is blocked from booking online for the
   * given business/team member. Openings-backed only; legacy locations return
   * false (block enforcement is handled server-side on create).
   */
  static isCustomerBookingBlocked = async (
    customerId: string,
    // Required: this decides whether a blocked customer is stopped, and it is
    // the business scope for the deposit gate. An optional appointment let
    // `appointment?.userId` arrive as `undefined` and silently resolve
    // Eddie's — a compile error is the right place to catch that.
    appointment: Pick<
      AppointmentForm,
      "userId" | "locationId" | "locationName" | "scheduleId"
    >,
  ): Promise<boolean> => {
    if (!customerId) {
      return false;
    }
    const gate = await getOpeningsBackedCustomerGate(
      customerId,
      appointment.userId,
      appointment,
    );
    return Boolean(gate?.block);
  };

  static getVerifiedAppointmentHistory = async (
    phoneNumber: string,
    code: string,
    appointment?: Pick<
      AppointmentForm,
      "userId" | "locationId" | "locationName" | "scheduleId"
    >,
  ): Promise<AppointmentHistoryData> =>
    getOpeningsBackedVerifiedAppointmentHistory(
      phoneNumber,
      code,
      appointment?.userId,
      appointment,
    );

  static sendVerificationCode = async (
    phoneNumber: string,
    appointment?: Pick<
      AppointmentForm,
      "userId" | "locationId" | "locationName" | "scheduleId"
    >,
    purpose?: string,
  ) => sendOpeningsBackedVerificationCode(phoneNumber, purpose, appointment);

  static verifyVerificationCode = async (
    phoneNumber: string,
    code: string,
    appointment?: Pick<
      AppointmentForm,
      "userId" | "locationId" | "locationName" | "scheduleId"
    >,
  ): Promise<boolean> =>
    verifyOpeningsBackedVerificationCode(phoneNumber, code, appointment);

  static sendServiceRequest = async (args: IServiceRequestArgs) =>
    createOpeningsBackedServiceRequest(args);

  static createPaymentMethod = async (
    args: ICreatePaymentMethodArgs,
    // Required: this CREATES the CRM customer in a specific business. It was
    // already dereferenced with `appointment!.userId`, so an omitted argument
    // was a runtime crash rather than the type error it should have been.
    appointment: Pick<
      AppointmentForm,
      "userId" | "locationId" | "locationName" | "scheduleId"
    >,
  ) => createOpeningsBackedPaymentMethod(args, appointment.userId, appointment);
  /*
    get customer's payment method.
  */
  static getPaymentMethod = async (
    phoneNumber: string,
    appointment?: Pick<
      AppointmentForm,
      "userId" | "locationId" | "locationName" | "scheduleId"
    >,
  ): Promise<{ paymentMethod: any }> => ({ paymentMethod: null });

  static addAppointmentNote = async (
    appointmentId: string,
    note: string,
    customerId?: string,
  ) => addOpeningsBackedAppointmentNote(appointmentId, customerId, note);

  static getStylistExperience = async (
    userId: string,
    options?: { businessId?: string; deviceId?: string; locationId?: string; photoSource?: "portfolio" | "published"; profileView?: boolean },
  ): Promise<UserExperienceResponse> =>
    getOpeningsBackedStylistExperience(userId, options);

  /**
   * Re-prices selected services (including a chosen option) against the
   * schedule/member they will actually be booked with. Rejects when the
   * target menu cannot authoritatively resolve every selection.
   */
  static getServicePricing = async (
    scheduleId: string,
    memberId: string,
    services: (Service & { option?: ServiceOption })[],
  ): Promise<(Service & { option?: ServiceOption })[]> =>
    getOpeningsBackedServicePricing(scheduleId, memberId, services);

  static getBlogArticles = async (): Promise<BlogListData[]> =>
    getOpeningsBackedBlogArticles();

  static getBlogArticle = async (articlePath: string): Promise<BlogData> =>
    getOpeningsBackedBlogArticle(articlePath);
}

export const removeChars = (str: string) => {
  return str.replace(/[^0-9]/g, "");
};
